import { FormControl } from '@mui/base';
import { Box, Button, Dialog, DialogContent, DialogTitle, FormControlLabel, Grid, IconButton, Radio, RadioGroup, Stack, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { primaryBlue } from '@styles/colors';
import { Dayjs } from 'dayjs';
import { Dispatch, FC, SetStateAction, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import { WeekIcon } from '../../vectors/index';

type SelectRepeatModalProps = {
  handleClose: () => void;
  setSelectedDays: Dispatch<SetStateAction<string[]>>;
  setRepeatDate: Dispatch<SetStateAction<Dayjs | null>>;
  setRepeatOccurence: Dispatch<SetStateAction<string | null>>;
  setRepeat: Dispatch<SetStateAction<boolean>>;
  setShowRepeatModal: Dispatch<SetStateAction<boolean>>;
  repeateDate: Dayjs | null;
  setRepeatEnd: Dispatch<SetStateAction<string>>;
  selectedDays: string[];
  isOpen: boolean;
  repeatEnd: string;
  showRepeatModal: boolean;
  repeateOccurence: string | null;

};

const SelectRepeatModal: FC<SelectRepeatModalProps> = ({
  setRepeatOccurence,
  repeateOccurence,
  setShowRepeatModal,
  showRepeatModal,
  isOpen,
  handleClose,
  setSelectedDays,
  selectedDays,
  repeatEnd,
  repeateDate,
  setRepeatDate,
  setRepeatEnd,
  setRepeat
}) => {
  const daysOfWeek = [
    { label: 'S', id: '6' },
    { label: 'M', id: '0' },
    { label: 'T', id: '1' },
    { label: 'W', id: '2' },
    { label: 'T', id: '3' },
    { label: 'F', id: '4' },
    { label: 'S', id: '5' },
  ];


  const [selectedChildDays, setSelecteChilddDays] = useState(selectedDays);
  const [repeatChildEnd, setRepeatChildEnd] = useState<string>(repeatEnd);
  const [repeateChildDate, setRepeatChildDate] = useState<Dayjs | null>(repeateDate);
  const [repeatChildOccurence, setRepeatChildOccurence] = useState<string | null>(repeateOccurence);

  const handleDateUpdate = (newDate: Dayjs | null) => {
    setRepeatChildDate(newDate);
  };

  const toggleDay = (id: string) => {
    if (selectedChildDays.includes(id)) {
      setSelecteChilddDays(selectedChildDays.filter((day) => day !== id));
    } else {
      setSelecteChilddDays([...selectedChildDays, id]);
    }
  };

  const handleRepeatSwitch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRepeatChildEnd(event.target.value);
    if (event.target.value === 'after') {
      setRepeatChildDate(null);
    }
    if (event.target.value === 'on') {
      setRepeatChildOccurence(null);
    }
    if (event.target.value === 'never') {
      setRepeatChildDate(null);
      setRepeatChildOccurence(null);
    }
  };

  const handleSubmit = () => {
    setSelectedDays(selectedChildDays);
    setRepeatEnd(repeatChildEnd);
    setRepeatDate(repeateChildDate);
    setRepeatOccurence(repeatChildOccurence);
    setRepeat(true);
    setShowRepeatModal(false);
  };

  const handleCloseModal = (event: any, reason: string) => {
    if (reason !== 'backdropClick') {
      handleClose()
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleCloseModal} maxWidth="xs" fullWidth>
      <DialogTitle>
        <IconButton sx={{ position: 'absolute', top: 5, right: 5 }} onClick={handleClose}>
          <CloseIcon />
        </IconButton>
        <br />
        <WeekIcon/>
        <Typography variant="h4" m={2}>
          Select Weeks
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography>
          Repeat on
        </Typography>
        <Grid container sx={{ mb: 1 }}>
          {daysOfWeek.map((day) => (
            <Grid item key={day.id}>
              <Box
                sx={{
                  borderRadius: '50%',
                  width: '35px',
                  height: '35px',
                  padding: "5px",
                  margin: '5px',
                  justifyContent: "center",
                  alignItems: "center",
                  alignSelf: "center",
                  textAlign: "center",
                  backgroundColor: selectedChildDays.includes(day.id) ? primaryBlue : '#f2f3f4',
                  color: selectedChildDays.includes(day.id) ? 'primary.contrastText' : '#bfc0c1',
                  '&:hover': {
                    cursor: selectedChildDays.includes(day.id) ? 'default' : 'pointer',
                  },
                }}
                onClick={() => toggleDay(day.id)}
              >
                {day.label}
              </Box>
            </Grid>
          ))}
        </Grid>

        <Typography>
          Ends
        </Typography>
        <Box>
          <FormControl>
            <RadioGroup
              aria-labelledby="demo-controlled-radio-buttons-group"
              name="controlled-radio-buttons-group"
              value={repeatChildEnd}
              onChange={handleRepeatSwitch}
            >
              <FormControlLabel value='never' control={<Radio />} label="Never" />
              <Stack direction="row" spacing={0} justifyContent='space-between'>
                <FormControlLabel value='on' control={<Radio />} label="On" />
                <DatePicker
                  disablePast
                  value={repeateChildDate}
                  disabled={repeatChildEnd !== 'on'}
                  onChange={handleDateUpdate}
                  sx={{ width: '50%' }}
                  format="MM/DD/YYYY"
                />
              </Stack>
              <Stack direction="row" spacing={0} justifyContent='space-between'>
                <FormControlLabel value='after' control={<Radio />} label="After" />
                <TextField
                  value={repeatChildOccurence ?? null}
                  onChange={(e) => setRepeatChildOccurence(e.target.value)}
                  type="number"
                  placeholder="Occurrences"
                  InputProps={{ inputProps: { min: 1 } }}
                  fullWidth
                  disabled={repeatChildEnd !== 'after'}
                  sx={{ width: "50%", mt: 1, }}
                />
              </Stack>
            </RadioGroup>
          </FormControl>
          <Button
            variant="contained"
            disabled={selectedChildDays.length === 0 || repeatChildEnd === 'on' && !repeateChildDate || repeatChildEnd === 'after' && !repeatChildOccurence}
            sx={{ borderRadius: 5, mt: 4, mb:2, width: '100%', alignSelf: 'center', textAlign: "center" }}
            startIcon={<DoneIcon />}
            onClick={handleSubmit}
          >
            Save
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SelectRepeatModal;
